'use client';

import {
  Box,
  Flex,
  freddyTheme,
  Tabs,
  TabsList,
  TabsListItem,
  TabsPanel,
  Typography
} from '@coolblue-development/freddy';
import selectProductSpecificationsTabTrigger from '@coolblue-development/ga4-events/triggers/ProductSpecifications/selectProductSpecificationsTabTrigger';
import viewElementTrigger from '@coolblue-development/ga4-events/triggers/ProductSpecifications/viewElementTrigger';
import { TrackWhenVisible, useAnalytics } from '@coolblue-development/next-analytics/client';
import { useFreddyTranslations } from '@coolblue-development/next-design-system-utils/client';
import { SquareProductImage } from '@coolblue-development/next-patterns/client';
import type { PropertyGroup, SubProducts } from '@features/product/typings';
import { useTranslation } from '@i18n/useTranslation';
import { ReactNode, useId } from 'react';

import { SearchField, SearchProvider, SearchResult } from './Search';

type SpecificationsProps = {
  productId: number;
  propertyGroups?: PropertyGroup[];
  productTypeId: number;
  subProducts: SubProducts[];
};

type SpecificationsSectionProps = {
  title: string;
  children: React.ReactNode;
};

type SingleProductSpecificationsProps = {
  productId: number;
  propertyGroups: PropertyGroup[];
  productTypeId: number;
};

type ComposedProductSpecificationsProps = {
  subProducts: SubProducts[];
};

export default function Specifications({
  productId,
  propertyGroups,
  productTypeId,
  subProducts
}: SpecificationsProps) {
  const { t } = useTranslation('webshop');
  const title = t('productPage.specifications');

  if (!propertyGroups) {
    return null;
  }

  if (subProducts.length > 0) {
    return (
      <SpecificationsSection title={title}>
        <ComposedProductSpecifications subProducts={subProducts} />
      </SpecificationsSection>
    );
  }

  return (
    <TrackWhenVisible trigger={viewElementTrigger({ sub_feature: 'specifications' })} threshold={0}>
      <SpecificationsSection title={title}>
        <SingleProductSpecifications
          productId={productId}
          propertyGroups={propertyGroups}
          productTypeId={productTypeId}
        />
      </SpecificationsSection>
    </TrackWhenVisible>
  );
}

function SingleProductSpecifications({
  productId,
  propertyGroups,
  productTypeId
}: SingleProductSpecificationsProps) {
  return (
    <SearchProvider>
      <SearchBox>
        <SearchField productId={productId} />
      </SearchBox>

      <SearchResult
        productId={productId}
        productTypeId={productTypeId}
        propertyGroups={propertyGroups}
      />
    </SearchProvider>
  );
}

function ComposedProductSpecifications({ subProducts }: ComposedProductSpecificationsProps) {
  const tabsTranslations = useFreddyTranslations('tabs');
  const { trackEvent } = useAnalytics();

  function handleTabChange(value: string) {
    const params = {
      index: subProducts.findIndex(subProduct => subProduct.product.id === Number(value)) + 1,
      total_indices: subProducts.length,
      item_id: Number(value)
    };
    trackEvent(selectProductSpecificationsTabTrigger(params));
  }

  return (
    <SearchProvider>
      <Tabs
        translations={tabsTranslations}
        defaultValue={subProducts[0].product.id.toString()}
        onChange={value => handleTabChange(value)}
      >
        <TabsList>
          {subProducts.map(subProduct => (
            <TabsListItem
              key={subProduct.product.id}
              value={subProduct.product.id.toString()}
              startAdornment={
                <SquareProductImage
                  imageId={subProduct.product.primaryImage.id}
                  background='transparent'
                  size='small'
                  alt='' // As the product name is displayed, the alt attribute is redundant here.
                  loading='lazy'
                />
              }
            >
              {subProduct.product.name}
            </TabsListItem>
          ))}
        </TabsList>

        {subProducts.map(subProduct => (
          <TabsPanel key={subProduct.product.id} value={subProduct.product.id.toString()}>
            <Flex direction='column' gap={500}>
              <SearchBox>
                <SearchField productId={subProduct.product.id} />
              </SearchBox>

              <SearchResult
                productId={subProduct.product.id}
                productTypeId={Number(subProduct.product.productType.productTypeId)}
                propertyGroups={subProduct.product.properties.propertyGroups}
              />
            </Flex>
          </TabsPanel>
        ))}
      </Tabs>
    </SearchProvider>
  );
}

function SpecificationsSection({ title, children }: SpecificationsSectionProps) {
  const titleId = useId();

  return (
    <Box hideUntil='md' component='section' id='product-specifications' aria-labelledby={titleId}>
      <Typography variant='h2' mb={400} id={titleId}>
        {title}
      </Typography>
      <Flex direction='column' gap={400} component='search' title={title}>
        {children}
      </Flex>
    </Box>
  );
}

const SearchBox = ({ children }: { children: ReactNode }) => (
  <Box sx={{ maxWidth: `calc(${freddyTheme.spacing[1000]} * 10)` }}>{children}</Box>
);
